import { useMemo, type CSSProperties, type ReactNode } from 'react'
import type { DayMark, WeekStartsOn } from '@/lib/eventCalendar'
import { cn } from '@/lib/utils'
import { PlansCalendarDay } from './PlansCalendarDay'
import { calendarWeeks } from './calendarWeeks'

/**
 * One page of `PlansCalendar`. Every week is always rendered; the calendar's
 * viewport clips to the shown ones and `style` shifts the page up to them, so
 * expanding is only a translate and a height. Rows outside the window are inert.
 */
export function PlansCalendarMonth({ month, currentMonth, today, weekStartsOn, expanded, marks, crewHidden, settled, renderDayPlans, openDay, onDayToggle, className, style, inert }: {
  month: string
  currentMonth: string
  today: string
  weekStartsOn: WeekStartsOn
  expanded: boolean
  marks: Map<string, DayMark>
  crewHidden?: boolean
  settled: boolean
  renderDayPlans: (day: string) => ReactNode
  /** The day whose popover is open; null on off-screen neighbor pages. */
  openDay: string | null
  onDayToggle: (day: string, open: boolean, marked: boolean) => void
  className?: string
  style?: CSSProperties
  /** An off-screen neighbor page: out of the tab order. */
  inert?: boolean
}) {
  const weeks = useMemo(() => calendarWeeks(month, currentMonth, today, weekStartsOn), [month, currentMonth, today, weekStartsOn])
  return (
    <div data-month={month} inert={inert} className={cn('w-full', className)} style={style}>
      {weeks.map(w => {
        const shown = expanded ? w.inMonth : w.inTwoWeeks
        return (
          <div key={w.days[0]} inert={!shown} aria-hidden={!shown} className="grid h-12 grid-cols-7">
            {w.days.map(d => (
              <PlansCalendarDay key={d} day={d} mark={marks.get(d)} today={today} crewHidden={crewHidden} settled={settled}
                outside={d.slice(0, 7) !== month.slice(0, 7)}
                open={d === openDay} plans={renderDayPlans} onToggle={onDayToggle} />
            ))}
          </div>
        )
      })}
    </div>
  )
}
